import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CognitoIdentityProviderClient,
  UpdateUserAttributesCommand,
  GetUserAttributeVerificationCodeCommand,
  ChangePasswordCommand,
  VerifyUserAttributeCommand,
  DeleteWebAuthnCredentialCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import config from "@/config";
import { useAuth } from "react-oidc-context";
import toast from "react-hot-toast";
import { useTranslations } from "next-intl";
import { listWebAuthnCredentials } from "@/lib/helpers";

export const useCognitoApi = () => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const t = useTranslations("toast");

  const getClient = () =>
    new CognitoIdentityProviderClient({
      region: config.cognito.region || "",
    });

  const requireAccessToken = () => {
    const accessToken = auth.user?.access_token;
    if (!accessToken) {
      toast.error(t("profile.noAccessToken"));
      throw new Error("No access token");
    }
    return accessToken;
  };

  const changePasswordMutation = useMutation({
    mutationFn: async (passwords: {
      existingPassword: string;
      newPassword: string;
    }) => {
      const accessToken = requireAccessToken();

      const client = getClient();

      const passwordChange = new ChangePasswordCommand({
        AccessToken: accessToken,
        PreviousPassword: passwords.existingPassword,
        ProposedPassword: passwords.newPassword,
      });

      return await client.send(passwordChange);
    },
    onSuccess: () => {
      // Toast handled in component
    },
    onError: (error, variables) => {
      console.error("Change Password Error: ", { error, variables });
    },
  });

  const updatePhoneMutation = useMutation({
    mutationFn: async (formattedPhone: string) => {
      const accessToken = requireAccessToken();

      const client = getClient();

      const command = new UpdateUserAttributesCommand({
        AccessToken: accessToken,
        UserAttributes: [
          {
            Name: "phone_number",
            Value: formattedPhone,
          },
        ],
      });

      return await client.send(command);
    },
    onSuccess: async () => {
      // Recall user profile api for updated values
      await queryClient.invalidateQueries({
        queryKey: ["get-user-profile"],
      });
    },
    onError: (error, variables) => {
      console.error("Update Phone Number Error: ", { error, variables });
    },
  });

  const sendOTPCodeMutation = useMutation({
    mutationFn: async (trigger?: string) => {
      const accessToken = requireAccessToken();

      const cognitoClient = new CognitoIdentityProviderClient({
        region: config.cognito.region,
        maxAttempts: 1,
      });

      const command = new GetUserAttributeVerificationCodeCommand({
        AccessToken: accessToken,
        AttributeName: "phone_number",
      });

      return await cognitoClient.send(command);
    },
    onSuccess: () => {},
    onError: (error, trigger) => {
      console.error("Send OTP Code Error: ", { error, trigger });
    },
  });

  const verifyOTPCodeMutation = useMutation({
    mutationFn: async (otpCode: string) => {
      const accessToken = requireAccessToken();

      const cognitoClient = new CognitoIdentityProviderClient({
        region: config.cognito.region,
        maxAttempts: 1,
      });

      const command = new VerifyUserAttributeCommand({
        AccessToken: accessToken,
        AttributeName: "phone_number",
        Code: otpCode,
      });

      return await cognitoClient.send(command);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["get-user-profile"],
      });
    },

    onError: (error) => {
      console.error("Verify OTP Error:", error);
    },
  });

  const listPasskeysQuery = useQuery({
    queryKey: ["list-passkeys"],
    queryFn: async () => {
      const accessToken = requireAccessToken();
      return listWebAuthnCredentials(accessToken);
    },
    enabled: !!auth.user?.access_token,
  });

  const redirectToAddPasskey = (options?: { reopenPatientModal?: boolean }) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("returnToProfile", "true");
      localStorage.setItem("returnToProfilePath", window.location.pathname);
      if (options?.reopenPatientModal) {
        localStorage.setItem("returnToPatientProfileModal", "true");
      }
    }
    window.location.href = `${config.cognito.cognitoDomain}/passkeys/add?client_id=${config.cognito.clientId}&redirect_uri=${config.cognito.redirectUri}`;
  };

  const deletePasskeyMutation = useMutation({
    mutationFn: async (credentialId: string) => {
      const accessToken = requireAccessToken();

      const client = getClient();

      const command = new DeleteWebAuthnCredentialCommand({
        AccessToken: accessToken,
        CredentialId: credentialId,
      });

      return await client.send(command);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["list-passkeys"] });
    },
    onError: (error) => {
      console.error("Delete Passkey Error: ", error);
    },
  });

  return {
    changePasswordMutation,
    updatePhoneMutation,
    sendOTPCodeMutation,
    verifyOTPCodeMutation,
    listPasskeysQuery,
    redirectToAddPasskey,
    deletePasskeyMutation,
  };
};
